/**
 * Metadatos de estado de Locación (US-2.2) — Admin Web.
 *
 * Mismo contrato que `ally-status.ts`: etiqueta + color + `colorDark` + `iconKey`,
 * para que el consumidor nunca dependa solo del color (WCAG). Los tipos `LocationStatus`
 * y `LocationSyncStatus` se derivan del contrato OpenAPI en `@goelectric/shared-types`.
 */
import type { LocationStatus, LocationSyncStatus } from '@goelectric/shared-types';
import { darkColors, neutralColors, semanticColors } from '../tokens';
import type { StatusIconKey } from './station-status';

export interface LocationStatusMeta {
  /** Etiqueta visible (obligatoria: no solo color). */
  label: string;
  description: string;
  /** Color para tema claro. */
  color: string;
  /** Color ajustado para tema oscuro. */
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Estado de gestión de la locación (`AdminLocationSummary.status`). */
export const LOCATION_STATUS_META: Record<LocationStatus, LocationStatusMeta> = {
  ACTIVE: {
    label: 'Activa',
    description: 'Ubicación habilitada para operar en la red.',
    color: semanticColors.success,
    colorDark: semanticColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Dada de baja',
    description: 'Ubicación deshabilitada (soft-delete).',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado de sincronización de la Location del core CitrineOS (ADR 0024). */
export const LOCATION_SYNC_META: Record<LocationSyncStatus, LocationStatusMeta> = {
  PENDIENTE: {
    label: 'Pendiente',
    description: 'Location aún no sincronizada con CitrineOS.',
    color: semanticColors.warning,
    colorDark: darkColors.accentOnDark,
    iconKey: 'info',
  },
  SYNCED: {
    label: 'Sincronizada',
    description: 'Location sincronizada con CitrineOS.',
    color: semanticColors.success,
    colorDark: semanticColors.success,
    iconKey: 'check',
  },
  ERROR: {
    label: 'Error de sincronización',
    description: 'Falló la sincronización de la Location con CitrineOS.',
    color: semanticColors.danger,
    colorDark: semanticColors.danger,
    iconKey: 'warning',
  },
};
